import React, { useState, useRef } from 'react'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import { cn } from '@/shared/utils/helpers'

export enum ConnectionStatus {
	STABLE = 'stable',
	POOR = 'poor',
	NO_CONNECTION = 'no_connection',
}

export interface HourlyData {
	hour: number
	status: ConnectionStatus
	timestamp: Date
}

export interface DailyData {
	day: string
	date: Date
	hourlyData?: HourlyData[]
	overallStatus?: ConnectionStatus
}

export interface ConnectionChartProps {
	data: DailyData[]
	mode: 'daily' | 'hourly'
	onModeChange?: (mode: 'daily' | 'hourly') => void
	showToggle?: boolean
	isLoading?: boolean
	variant?: 'card' | 'details'
}

const getStatusColor = (status: ConnectionStatus) => {
	switch (status) {
		case ConnectionStatus.STABLE:
			return 'bg-status-success'
		case ConnectionStatus.POOR:
			return 'bg-status-warning'
		case ConnectionStatus.NO_CONNECTION:
			return 'bg-shade'
		default:
			return 'bg-shade'
	}
}

const getStatusLabel = (
	status: ConnectionStatus,
	t: (key: string) => string
) => {
	switch (status) {
		case ConnectionStatus.STABLE:
			return t('connection.status.stable')
		case ConnectionStatus.POOR:
			return t('connection.status.poor')
		case ConnectionStatus.NO_CONNECTION:
			return t('connection.status.noConnection')
		default:
			return 'Unknown'
	}
}

const getStatusDotColor = (status: ConnectionStatus) => {
	switch (status) {
		case ConnectionStatus.STABLE:
			return 'bg-status-success'
		case ConnectionStatus.POOR:
			return 'bg-status-warning'
		case ConnectionStatus.NO_CONNECTION:
			return 'bg-content-disabled'
		default:
			return 'bg-content-disabled'
	}
}

const formatDate = (date: Date, locale: string) => {
	const weekday = date.toLocaleString(locale, { weekday: 'short' })
	const day = date.getDate()
	const month = date.toLocaleString(locale, { month: 'short' })
	return `${weekday}, ${day} ${month}`
}

const formatTime = (hour: number) => {
	return `${hour.toString().padStart(2, '0')}:00 hs`
}

const formatDayName = (date: Date, locale: string) => {
	const dayName = date.toLocaleString(locale, { weekday: 'long' })
	return dayName.charAt(0).toUpperCase() + dayName.slice(1)
}

export const ConnectionChart: React.FC<ConnectionChartProps> = ({
	data,
	mode,
	onModeChange,
	showToggle = true,
	isLoading = false,
	variant = 'card',
}) => {
	const { t, currentLanguage } = useTranslation('zones')
	const [hoveredBlock, setHoveredBlock] = useState<{
		dayIndex: number
		hourIndex?: number
	} | null>(null)
	const [tooltipData, setTooltipData] = useState<{
		x: number
		y: number
		hour?: number
		status: ConnectionStatus
		date?: Date
	} | null>(null)
	const containerRef = useRef<HTMLDivElement>(null)

	// Skeleton loading state for daily mode
	if (isLoading && mode === 'daily') {
		return (
			<div className="space-y-3">
				<div className="flex items-center justify-between">
					<Skeleton className="h-[18px] w-48" />
					{showToggle && <Skeleton className="h-4 w-20" />}
				</div>
				<div className="relative">
					<div className="flex items-center gap-2">
						{Array.from({ length: 7 }).map((_, dayIndex) => (
							<div key={dayIndex} className="flex-1">
								<Skeleton className="h-10 w-full rounded-md" />
							</div>
						))}
					</div>
					<div className="flex items-center justify-between mt-2">
						<Skeleton className="h-[18px] w-16" />
						<Skeleton className="h-[18px] w-16" />
					</div>
				</div>
			</div>
		)
	}

	// Skeleton loading state for hourly mode
	if (isLoading && mode === 'hourly') {
		return (
			<div className="space-y-3">
				<div className="flex items-center justify-between">
					<Skeleton className="h-[18px] w-48" />
					{showToggle && <Skeleton className="h-4 w-20" />}
				</div>
				<div className="space-y-3">
					{Array.from({ length: 7 }).map((_, dayIndex) => (
						<div key={dayIndex} className="relative">
							<div className="flex items-center gap-1">
								{Array.from({ length: 24 }).map((_, hourIndex) => (
									<Skeleton
										key={hourIndex}
										className="flex-1 h-10 rounded-md"
									/>
								))}
							</div>
							<div className="flex items-center justify-between mt-2">
								<Skeleton className="h-[18px] w-24" />
								<Skeleton className="h-4 w-16" />
							</div>
						</div>
					))}
				</div>
			</div>
		)
	}

	if (mode === 'daily') {
		// Show 7 blocks representing 7 days
		// Reverse data to show chronological order (Oldest -> Newest) for the horizontal chart
		const chronologicalData = [...data].reverse()

		return (
			<div className={cn('space-y-3', variant === 'details' && 'mb-12')}>
				<div className="flex items-center justify-between">
					<div
						className={cn(
							'text-xs text-muted-foreground font-medium',
							variant === 'details' &&
								'text-content-tertiary md:text-white md:text-base'
						)}
					>
						{t('connection.chart.last7Days')}
					</div>
				</div>
				<div className="relative">
					<div className="flex items-center gap-2">
						{chronologicalData.map((day, index) => {
							const status = day.overallStatus || ConnectionStatus.NO_CONNECTION
							const isHovered = hoveredBlock?.dayIndex === index
							const connectedHours =
								day.hourlyData?.filter(
									(h) => h.status === ConnectionStatus.STABLE
								).length ?? 0

							return (
								<div key={index} className="relative flex-1">
									<div
										className={cn(
											'transition-all cursor-pointer py-5 rounded-md',
											getStatusColor(status),
											isHovered && 'ring-2 ring-white/40'
										)}
										onMouseEnter={() => setHoveredBlock({ dayIndex: index })}
										onMouseLeave={() => setHoveredBlock(null)}
									/>
									{isHovered && (
										<div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-50">
											<div className="bg-popover border border-border rounded-lg pl-4 py-2.5 pr-4 shadow-2xl min-w-[160px]">
												<div className="text-[13px] mb-1.5 font-medium">
													{formatDate(day.date, currentLanguage)}
												</div>
												<div className="flex items-center gap-2">
													<span
														className={cn(
															'h-2 w-2 rounded-full',
															getStatusDotColor(status)
														)}
													/>
													<span className="text-[13px]">
														{getStatusLabel(status, t)}
													</span>
												</div>
												<div className="text-[12px] text-content-tertiary mt-1">
													{connectedHours}h{' '}
													{currentLanguage === 'es' ? 'conectado' : 'connected'}
												</div>
											</div>
										</div>
									)}
								</div>
							)
						})}
					</div>
					{chronologicalData.length > 0 && (
						<div className="flex items-center justify-between mt-2">
							<div className="text-[13px] text-content-tertiary">
								{formatDayName(chronologicalData[0].date, currentLanguage)}
							</div>
							<div className="text-[13px] text-content-tertiary">
								{formatDayName(
									chronologicalData[chronologicalData.length - 1].date,
									currentLanguage
								)}
							</div>
						</div>
					)}
				</div>
			</div>
		)
	}

	// Show rows of 24 hourly blocks each (one row per day)
	const handleHourMouseEnter = (
		e: React.MouseEvent<HTMLDivElement>,
		dayIndex: number,
		hourIndex: number,
		hourData: HourlyData
	) => {
		const rect = e.currentTarget.getBoundingClientRect()
		const containerRect = containerRef.current?.getBoundingClientRect()

		if (containerRect) {
			setTooltipData({
				x: rect.left + rect.width / 2 - containerRect.left,
				y: rect.top - containerRect.top,
				hour: hourData.hour,
				status: hourData.status,
			})
		}
		setHoveredBlock({ dayIndex, hourIndex })
	}

	const handleMouseLeave = () => {
		setHoveredBlock(null)
		setTooltipData(null)
	}

	return (
		<div className="space-y-3 relative" ref={containerRef}>
			<div className="flex items-center justify-between">
				<div className="text-[13px] font-medium">
					{t('connection.chart.lastDays', { count: data.length })}
				</div>
				{showToggle && onModeChange && (
					<button
						onClick={() => onModeChange('daily')}
						className="text-[11px] text-content-tertiary hover:text-foreground transition-colors"
					>
						{t('connection.chart.viewDaily')}
					</button>
				)}
			</div>
			<div className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto">
				{data.map((day, dayIndex) => (
					<div key={dayIndex} className="relative">
						<div className="text-base font-medium mb-1">
							{formatDate(day.date, currentLanguage)}
						</div>
						<div className="flex items-center gap-2">
							{day.hourlyData && day.hourlyData.length > 0
								? day.hourlyData.map((hourData, hourIndex) => {
										const isHovered =
											hoveredBlock?.dayIndex === dayIndex &&
											hoveredBlock?.hourIndex === hourIndex

										return (
											<div key={hourIndex} className="relative flex-1">
												<div
													className={cn(
														'h-10 rounded-lg transition-all cursor-pointer',
														getStatusColor(hourData.status),
														isHovered && 'ring-2 ring-white/40'
													)}
													onMouseEnter={(e) =>
														handleHourMouseEnter(
															e,
															dayIndex,
															hourIndex,
															hourData
														)
													}
													onMouseLeave={handleMouseLeave}
												/>
											</div>
										)
									})
								: // Fallback to 24 empty blocks if no data
									Array.from({ length: 24 }).map((_, hourIndex) => (
										<div
											key={hourIndex}
											className="flex-1 h-10 rounded-md bg-shade"
										/>
									))}
						</div>
						<div className="flex items-center justify-between mt-1">
							<div className="text-[11px] text-content-tertiary">00hs</div>
							<div className="text-[11px] text-content-tertiary">23hs</div>
						</div>
					</div>
				))}
			</div>

			{/* Tooltip rendered outside scrollable container */}
			{tooltipData && (
				<div
					className="absolute pointer-events-none z-50"
					style={{
						left: tooltipData.x,
						top: tooltipData.y - 12,
						transform: 'translate(-50%, -100%)',
					}}
				>
					<div className="bg-popover border border-border rounded-lg px-4 py-2.5 shadow-2xl min-w-[140px]">
						<div className="text-[13px] mb-1.5 font-medium">
							{tooltipData.hour !== undefined && formatTime(tooltipData.hour)}
						</div>
						<div className="flex items-center gap-2">
							<span
								className={cn(
									'h-2 w-2 rounded-full',
									getStatusDotColor(tooltipData.status)
								)}
							/>
							<span className="text-[13px]">
								{getStatusLabel(tooltipData.status, t)}
							</span>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default ConnectionChart
